/**
 * Thin wrapper around browser-image-compression for venue form uploads.
 * Compresses on file selection (not on submit) using a Web Worker when needed.
 */
(function (global) {
  const SKIP_MIME_TYPES = new Set(['image/svg+xml', 'image/gif']);
  const SKIP_EXTENSIONS = new Set(['.svg', '.gif', '.psd']);
  const DEFAULT_MAX_DIMENSION = 1920;
  const DEFAULT_QUALITY = 0.82;

  const compressionQueue = {
    maxConcurrent: 2,
    running: 0,
    queue: [],

    enqueue(fn) {
      return new Promise((resolve, reject) => {
        this.queue.push({ fn, resolve, reject });
        this.drain();
      });
    },

    drain() {
      while (this.running < this.maxConcurrent && this.queue.length > 0) {
        const job = this.queue.shift();
        this.running += 1;
        Promise.resolve()
          .then(() => job.fn())
          .then(job.resolve, job.reject)
          .finally(() => {
            this.running -= 1;
            this.drain();
          });
      }
    },
  };

  function getExtension(name) {
    if (!name) return '';
    const base = name.split(/[?#]/)[0].split('/').pop() || '';
    const dot = base.lastIndexOf('.');
    if (dot <= 0) return '';
    return base.slice(dot).toLowerCase();
  }

  function replaceExtension(name, newExt) {
    const ext = getExtension(name);
    if (!ext) return `${name}${newExt}`;
    return name.slice(0, -ext.length) + newExt;
  }

  function isCompressibleImage(file) {
    if (!file || !file.type || !file.type.startsWith('image/')) return false;
    if (SKIP_MIME_TYPES.has(file.type)) return false;
    const ext = getExtension(file.name);
    if (ext && SKIP_EXTENSIONS.has(ext)) return false;
    return true;
  }

  async function readImageDimensions(file) {
    const bitmap = await createImageBitmap(file);
    const dimensions = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return dimensions;
  }

  async function getCompressionDecision(file, options) {
    const dimensions = await readImageDimensions(file);

    if (file.size > options.maxBytes) {
      return { needed: true, dimensions };
    }

    if (dimensions.width > options.maxDimension || dimensions.height > options.maxDimension) {
      return { needed: true, dimensions };
    }

    return { needed: false, dimensions };
  }

  function getCompressionLib() {
    return global.imageCompression;
  }

  function getLibUrl() {
    return global.__IMAGE_COMPRESSION_LIB_URL || null;
  }

  async function compressImageIfNeeded(file, options = {}) {
    if (!isCompressibleImage(file)) return file;

    const compress = getCompressionLib();
    if (!compress) return file;

    const config = {
      maxBytes: options.maxBytes ?? 1024 * 1024,
      maxDimension: options.maxDimension ?? DEFAULT_MAX_DIMENSION,
      quality: options.quality ?? DEFAULT_QUALITY,
    };

    let decision;
    try {
      decision = await getCompressionDecision(file, config);
      if (!decision.needed) return file;
    } catch (error) {
      return file;
    }

    return compressionQueue.enqueue(async () => {
      try {
        const libURL = getLibUrl();
        const compressedFile = await compress(file, {
          maxSizeMB: config.maxBytes / (1024 * 1024),
          maxWidthOrHeight: config.maxDimension,
          initialQuality: config.quality,
          useWebWorker: true,
          fileType: 'image/jpeg',
          ...(libURL ? { libURL } : {}),
        });

        const outputType = compressedFile.type || 'image/jpeg';
        const outputName =
          outputType === 'image/jpeg'
            ? replaceExtension(file.name, '.jpg')
            : compressedFile.name || file.name;

        const outputFile =
          compressedFile.name === outputName && compressedFile.type === outputType
            ? compressedFile
            : new File([compressedFile], outputName, {
                type: outputType,
                lastModified: compressedFile.lastModified ?? Date.now(),
              });

        return outputFile;
      } catch (error) {
        return file;
      }
    });
  }

  global.ImageCompressor = {
    compressImageIfNeeded,
    isCompressibleImage,
  };
})(window);
